// Мастер «Поставить на объект»: объект → пост → юрлицо → даты → тариф.
//
// Пост выбирается ВНУТРИ объекта, а не из общего списка постов: «КПП» есть на каждом втором
// объекте, и общий список из полусотни одноимённых постов — прямая дорога поставить человека
// не туда. Поэтому объект здесь обязателен и выбирается первым.
//
// Ответ сервера несёт две вещи, которые обязаны доехать до глаз:
//   * **предупреждения** (занят на другом объекте, блокирующий долг, нет удостоверения) — они
//     мешают, но не запрещают, решает человек. Показываются ПОСЛЕ постановки, потому что
//     сервер их и считает при постановке;
//   * **изменение укомплектованности** объекта: было столько, стало столько, требуется
//     столько. Без этой строки постановка выглядит как действие в пустоту.

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiCheckboxCircleLine, RiErrorWarningLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import type { AssignmentIn, AssignmentOut } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { usePostsQuery, useLegalEntitiesQuery, useSitesQuery } from '@/shared/org/queries';
import {
  ДАТА_ПО_НЕДОПИСАНА,
  датаПоНедописана,
  последствиеДатыПо,
} from '@/shared/people/последний-день';
import { can } from '@/shared/rbac/permissions';
import { formatDateOnly } from '@/shared/time/format';
import * as Alert from '@/shared/ui/alert';
import { Skeleton } from '@/shared/ui/screen-state';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';

import type { RateFieldName } from '../components/RateFields';
import { EMPTY_RATE_FORM, RateFields, buildRateValues } from '../components/RateFields';
import { StaffingNotice, WarningsNotice } from '../components/AssignmentResult';
import { newIdempotencyKey, useCreateAssignmentMutation, useEmployeeQuery } from '../queries';

const EDIT_PERMISSION = 'core.employee.edit';

type FieldName = 'site' | 'post' | 'legal_entity' | 'date_from' | 'date_to' | RateFieldName;

export function AssignPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const permissions = useAuthStore((state) => state.permissions);
  const allowed = can(permissions, EDIT_PERMISSION);
  const employee = useEmployeeQuery(id);

  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [siteId, setSiteId] = useState(NO_VALUE);
  const [postId, setPostId] = useState(NO_VALUE);
  const [legalEntityId, setLegalEntityId] = useState(NO_VALUE);
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [dateTo, setDateTo] = useState('');
  // Что стоит в поле «По» на экране. Поле отдаёт наружу пустую строку и на недописанную дату, а
  // пустое «По» значит «без даты окончания»: «30.09.20» молча стало бы бессрочным назначением.
  const [набраноПо, setНабраноПо] = useState('');
  const [rate, setRate] = useState(EMPTY_RATE_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);
  const [created, setCreated] = useState<AssignmentOut | null>(null);

  const sites = useSitesQuery({ active: true });
  const posts = usePostsQuery(siteId === NO_VALUE ? undefined : siteId);
  const legalEntities = useLegalEntitiesQuery();
  const mutation = useCreateAssignmentMutation(idempotencyKey);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Ставить людей на объект может тот, у кого есть право на правку карточек. Попросите администратора выдать его.',
      );
      return;
    }
    if (mutation.isPending) {
      setGateNote('Назначение уже создаётся, ждём ответ сервера. Второй запрос не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (siteId === NO_VALUE) {
      next.site = 'Выберите объект: пост принадлежит объекту, без него список постов пуст.';
    }
    if (postId === NO_VALUE) {
      next.post = 'Выберите пост: от него зависят часы, разряд и норматив.';
    }
    if (legalEntityId === NO_VALUE) {
      next.legal_entity = 'Выберите юрлицо: от него зависит, в чью ведомость попадёт человек.';
    }
    if (dateFrom === '') {
      next.date_from = 'Укажите, с какого дня человек заступает.';
    }
    if (dateTo !== '' && dateTo < dateFrom) {
      next.date_from = 'Последний день раньше первого: проверьте даты.';
    }
    if (датаПоНедописана(dateTo, набраноПо)) {
      next.date_to = ДАТА_ПО_НЕДОПИСАНА;
    }

    const rateValues = buildRateValues(rate);
    if (!rateValues.ok) {
      Object.assign(next, rateValues.errors);
    }

    if (Object.keys(next).length > 0 || !rateValues.ok) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    const body: AssignmentIn = {
      employee_id: id,
      post_id: postId,
      legal_entity_id: legalEntityId,
      date_from: dateFrom,
      date_to: dateTo === '' ? null : dateTo,
      ...rateValues.values,
    };
    mutation.mutate(body, {
      onSuccess: (assignment) => {
        // Панель НЕ закрывается сама: в ответе приехали предупреждения и изменение
        // укомплектованности, и закрыть её сейчас значило бы спрятать их от человека.
        setCreated(assignment);
        // Следующая постановка — новая форма, значит новый ключ идемпотентности.
        setIdempotencyKey(newIdempotencyKey());
      },
    });
  }

  if (created !== null) {
    return (
      <div className='flex flex-col gap-3'>
        <Alert.Root variant='light' status='success' size='small' role='status'>
          <Alert.Icon as={RiCheckboxCircleLine} />
          <span>
            Человек поставлен на «{created.site_name} · {created.post_name}» с{' '}
            {formatDateOnly(created.date_from)}: {created.employee_name}.
          </span>
        </Alert.Root>
        <WarningsNotice warnings={created.warnings ?? []} />
        <StaffingNotice staffing={created.staffing ?? null} siteName={created.site_name} />
        <div className='flex items-center gap-2 pt-1'>
          <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={onClose}>
            Готово
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='small'
            onClick={() => setCreated(null)}
          >
            Поставить ещё на один пост
          </Button.Root>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {/* Пока имя едет — скелет, а не слово «загружаем…»: серая строка текста на месте
          будущего имени читается как ЗНАЧЕНИЕ («чьё это заявление? загружаем карточку»), а
          человек в этот момент уже начал заполнять форму. Скелет говорит «сюда приедет», а
          не подставляет ответ. */}
      {/* Скелет показывается ТОЛЬКО пока запрос идёт. Условие «данных нет» держало бы его
          вечно: карточка может не приехать вовсе — например, панель открыли по ссылке на
          человека, которого больше не видно по правам. Вечный скелет читается как «сейчас
          догрузится» и не догружается никогда. */}
      {employee.data === undefined && employee.isPending ? (
        <Skeleton className='h-4 w-40' />
      ) : employee.data === undefined ? null : (
        <p className='text-paragraph-xs text-text-sub-600'>{employee.data.display_name}</p>
      )}

      {!allowed && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            Права на правку карточек у вас нет: форму можно заполнить, но назначение не сохранится.
            Право выдаёт администратор.
          </span>
        </Alert.Root>
      )}

      {employee.data?.has_debt === true && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiErrorWarningLine} />
          <span>
            На человеке есть непогашенный долг. Постановке это не мешает, но сервер вернёт
            предупреждение — решать вам.
          </span>
        </Alert.Root>
      )}

      <SelectField
        id='assign-site'
        label='Объект'
        required
        value={siteId}
        onChange={(value) => {
          setSiteId(value);
          // Пост принадлежит объекту: сменили объект — прежний пост чужой.
          setPostId(NO_VALUE);
        }}
        options={(sites.data?.items ?? []).map((site) => ({ value: site.id, label: site.name }))}
        placeholder='Выберите объект'
        pending={sites.isPending}
        failed={sites.isError}
        failedError={sites.error as ApiError | null}
        emptyNote='Действующих объектов нет: ставить человека некуда. Заведите объект в разделе «Объекты».'
        error={errors.site}
      />

      <SelectField
        id='assign-post'
        label='Пост'
        required
        value={postId}
        onChange={setPostId}
        options={(posts.data?.items ?? []).map((post) => ({
          value: post.id,
          label:
            post.required_guard_category === null
              ? post.name
              : `${post.name} · ${post.required_guard_category} разряд`,
        }))}
        placeholder={siteId === NO_VALUE ? 'Сначала выберите объект' : 'Выберите пост'}
        pending={siteId !== NO_VALUE && posts.isPending}
        failed={posts.isError}
        failedError={posts.error as ApiError | null}
        // Подсказка «постов нет» появляется, только когда объект УЖЕ выбран. Пока он не
        // выбран, список постов пуст по другой причине — грузить нечего, — и подсказка
        // спорила с плейсхолдером в том же поле: «Сначала выберите объект» и тут же «у
        // объекта нет ни одного поста». Два сообщения об одном, одно из них неверное.
        emptyNote={
          siteId === NO_VALUE
            ? undefined
            : 'У объекта нет ни одного поста: заведите пост в карточке объекта, иначе ставить человека не на что.'
        }
        error={errors.post}
      />

      <SelectField
        id='assign-legal-entity'
        label='Юрлицо'
        required
        value={legalEntityId}
        onChange={setLegalEntityId}
        options={(legalEntities.data?.items ?? []).map((item) => ({
          value: item.id,
          label: item.short_name ?? item.name,
        }))}
        placeholder='Выберите юрлицо'
        pending={legalEntities.isPending}
        failed={legalEntities.isError}
        failedError={legalEntities.error as ApiError | null}
        emptyNote='Юрлиц в справочнике нет. Их заводит администратор в настройках системы.'
        error={errors.legal_entity}
        hint='От юрлица зависит, в чью ведомость попадёт человек.'
      />

      <DateField
        id='assign-date-from'
        label='Заступает с'
        required
        value={dateFrom}
        onChange={setDateFrom}
        error={errors.date_from}
      />

      {/* Обёртка ловит набор в поле «По»: само поле отдаёт наружу только целую дату. */}
      <div onInput={(event) => setНабраноПо((event.target as HTMLInputElement).value)}>
        <DateField
          id='assign-date-to'
          label='По'
          value={dateTo}
          onChange={setDateTo}
          error={errors.date_to}
          // «По» — последний день ВКЛЮЧИТЕЛЬНО, и под полем сказано, с какого дня человека не
          // будет в табеле: 10.10.2026 заказчик понял «По 30.09» как «период работы».
          hint={
            последствиеДатыПо(dateTo) ??
            'Можно оставить пустым: назначение будет считаться живым, пока его не закроют.'
          }
        />
      </div>

      <RateFields idPrefix='assign' value={rate} onChange={setRate} errors={errors} />

      {mutation.isError && <ErrorAlert error={mutation.error} />}

      {gateNote !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {gateNote}
        </Hint.Root>
      )}

      <p className='text-paragraph-xs text-text-sub-600'>
        Посты объекта берутся из{' '}
        <Link to='/sites/objects' className='text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'>
          раздела «Объекты»
        </Link>
        : если нужного поста нет, его заводят там.
      </p>

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {mutation.isPending ? 'Ставим на объект…' : 'Поставить на объект'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          Отмена
        </Button.Root>
      </div>
    </form>
  );
}
